import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import classes from "./StudioProfile.module.css"
import Carousel from "./Carousel"
import starIcon from "../../public/HomepageVectors/studioIcons/7 Stern Icon.svg"
import { getStudioPriceDisplay } from "../../util/studioPricing";
import { trackStudioSelected } from "../../util/tracking";
// import { min } from 'lodash';

const StudioProfile = ({ renderedBy, studio, isLoading, fromSearch = false, fromWishlist = false }) => {
    const [contentLoaded, setContentLoaded] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    // Compute lowest possible visible price for StudioErstellen preview
    const lowestPrice = useMemo(() => {
        if (!studio) return null;

        const safeNumber = (value) => {
            const n = parseFloat(value);
            return Number.isFinite(n) && n > 0 ? n : null;
        };

        const getGroupPrice = (group) => {
            if (!group) return null;
            // Creation wizard uses "price", backend tiered pricing uses "pricePerHour"
            if (group.price != null) return safeNumber(group.price);
            if (group.pricePerHour != null) return safeNumber(group.pricePerHour);
            return null;
        };

        // Hourly pricing
        if (studio.pricingType === 'stundenpreis') {
            const tierGroups = Array.isArray(studio.priceGroups) && studio.priceGroups.length > 0
                ? studio.priceGroups
                : (Array.isArray(studio.tieredPricing) ? studio.tieredPricing : []);

            if (studio.adjustPriceByPeople && tierGroups.length > 0) {
                let min = null;
                tierGroups.forEach(group => {
                    const n = getGroupPrice(group);
                    if (n !== null && (min === null || n < min)) {
                        min = n;
                    }
                });
                return min;
            }
            return safeNumber(studio.pricePerHour);
        }

        // Package pricing: find minimal package price across all packages / groups
        if (studio.pricingType === 'paketpreise' && Array.isArray(studio.packages) && studio.packages.length > 0) {
            const hasGroups = studio.adjustPriceByPeople && Array.isArray(studio.priceGroups) && studio.priceGroups.length > 0;
            const groups = hasGroups
                ? studio.priceGroups
                : [{
                    id: 'all',
                    from: 1,
                    to: parseInt(studio.capacity || 0, 10) || 0
                }];

            let min = null;

            studio.packages.forEach(pkg => {
                if (!pkg || !pkg.prices) return;
                groups.forEach(group => {
                    const groupId = hasGroups ? group.id : 'all';
                    const price = pkg.prices[groupId];
                    const n = safeNumber(price);
                    if (n !== null && (min === null || n < min)) {
                        min = n;
                    }
                });
            });

            return min;
        }

        return null;
    }, [studio]);

    // For StudioErstellen preview, only show "ab" when there are real tiers (people-based hourly or packages)
    const showAbPrefixForPreview = useMemo(() => {
        if (!studio) return false;
        const hasTieredHourly = studio.pricingType === 'stundenpreis'
            && studio.adjustPriceByPeople
            && (
                (Array.isArray(studio.priceGroups) && studio.priceGroups.length > 0) ||
                (Array.isArray(studio.tieredPricing) && studio.tieredPricing.length > 0)
            );
        const hasPackagePricing = studio.pricingType === 'paketpreise'
            && Array.isArray(studio.packages)
            && studio.packages.length > 0;
        return hasTieredHourly || hasPackagePricing;
    }, [studio]);

    const previewDisplayPrice = useMemo(() => {
        if (!studio) return null;
        const base = lowestPrice != null
            ? lowestPrice
            : (studio.pricePerHour != null ? studio.pricePerHour : null);
        if (base == null) return null;
        const prefix = showAbPrefixForPreview ? 'ab ' : '';
        return `${prefix}${base}€`;
    }, [studio, lowestPrice, showAbPrefixForPreview]);

    const backendPriceDisplay = useMemo(() => getStudioPriceDisplay(studio), [studio]);
    
    // Format studio data based on renderedBy prop
    const formattedStudio = useMemo(() => {
        if (!studio) return null;

        // For StudioErstellen, use base64 images directly
        if (renderedBy === 'StudioErstellen') {
            return {
                ...studio,
                // Each image in the array should be an object with a url property
                images: studio.images.map((base64String, index) => ({
                    url: base64String,
                    id: `preview-${index}`
                }))
            };
        }

        // For other cases, keep the original format
        return studio;
    }, [studio, renderedBy]);
    
    const isMapOrWishlist = renderedBy === 'MapComponent' || renderedBy === 'Wishlist';

    const containerStyle = useMemo(() => {
        let style = {};
        
        // Special cases for different rendering contexts
        if (isMapOrWishlist) {
            style = { width: "100%", maxWidth: "100%" };
        } else if (renderedBy === 'StudioErstellen') {
            style = { width: "350px", maxWidth: "350px" };
        } else if (isLoading || !contentLoaded) {
            style = { width: "100%" };
        }
        
        return style;
    }, [isMapOrWishlist, renderedBy, isLoading, contentLoaded]);
    
    // Memoize details container style based on props and window width
    const detailsStyle = useMemo(() => {
        if (renderedBy === 'MapComponent') {
            return { padding: "0 16px 16px 10px" };
        } else if (renderedBy === 'StudioErstellen') {
            return { padding: "0" };
        } else if (renderedBy === 'Wishlist' && windowWidth < 481) {
            return { padding: "0 24px", paddingTop: "0.75rem" };
        }
        return {};
    }, [renderedBy, windowWidth]);
    
    // Memoize title container style
    const titleStyle = useMemo(() => {
        return renderedBy === 'Wishlist' && windowWidth < 481 ? { margin: "0" } : {};
    }, [renderedBy, windowWidth]);
    
    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Reset image/text reveal when the card switches to another studio
    useEffect(() => {
        setContentLoaded(false);
    }, [studio?.id, isLoading]);

    // Do not drop the image skeleton on a timer: Carousel keeps imgs at opacity 0 until load,
    // which produced a blank white box. Wait for the first image (onImagesLoaded) or skip if none.
    useEffect(() => {
        if (isLoading || !studio) return;
        const imgs = studio.images;
        const hasImages = Array.isArray(imgs) && imgs.length > 0;
        if (!hasImages) {
            setContentLoaded(true);
        }
    }, [isLoading, studio?.id, studio?.images]);

    // Early return if studio is null
    if (!studio) {
        return null;
    }

    const studioPath = `/studio/${studio?.id || ''}`;
    const markOpenedFromSearch = () => {
        if (!fromSearch) return;
        trackStudioSelected({
            studio,
            source: "search_results",
        });
        try {
            sessionStorage.setItem('stuStudioBackFrom', 'search');
        } catch (_) {
            /* ignore */
        }
    };
    const studioListLinkTo =
        fromSearch && !isLoading ? { pathname: studioPath, state: { from: 'search' } } : studioPath;

    return (
        <div
            className={`${classes.container} ${isMapOrWishlist ? classes.mapWishlistContainer : ''} ${isLoading ? classes.loading : classes.fadeIn} ${fromWishlist ? classes.wishlistContainer : ''}`}
            style={containerStyle}
        >
            <div 
                className={`${classes.imageContainer} ${!contentLoaded ? classes.skeleton : ''}`} 
                style={isMapOrWishlist ? { aspectRatio: "11/7" } : {}}
            >
                <Carousel 
                    studio={isLoading ? null : formattedStudio} 
                    renderedBy={renderedBy}
                    isLoading={isLoading}
                    linkFromSearch={fromSearch}
                    onImagesLoaded={() => setContentLoaded(true)}
                />
            </div>
            {renderedBy === 'StudioErstellen' ? (
                <div 
                    className={classes.detailsContainer}
                    style={{...detailsStyle, cursor: isLoading ? 'default' : 'pointer'}}
                >
                    <div 
                        style={titleStyle} 
                        className={`${classes.titleContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}
                    >
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldMiddle">{studio?.StudioName}</span>
                            </>
                        )}
                    </div>
                    <div className={`${classes.locationContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <span className="regularSmall">{studio?.city || studio?.location}</span>
                        )}
                    </div>
                    <div className={`${classes.priceContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldSmall">
                                    {previewDisplayPrice}
                                </span>
                                <span className="regularSmall">pro Stunde</span>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <Link 
                    target={isLoading ? '_self' : '_blank'}
                    to={isLoading ? '#' : studioListLinkTo}
                    className={classes.detailsContainer}
                    style={{...detailsStyle, cursor: isLoading ? 'default' : 'pointer'}}
                    onClick={isLoading ? () => {} : markOpenedFromSearch}
                >
                    <div 
                        style={titleStyle} 
                        className={`${classes.titleContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}
                    >
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldMiddle">{studio?.StudioName}</span>
                                {renderedBy !== 'StudioErstellen' && studio?.rating ? (
                                    <div className={classes.rattingContainer}>
                                        <span>{studio?.rating}</span>
                                        <img src={starIcon} alt="star"/>
                                    </div>
                                ) : (
                                    <div className={classes.rattingContainer}>
                                        <span>Neu</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${classes.locationContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <span className="regularSmall">{studio?.city}</span>
                        )}
                    </div>
                    <div className={`${classes.priceContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldSmall">{backendPriceDisplay.label}</span>
                                <span className="regularSmall">{backendPriceDisplay.unit}</span>
                            </>
                        )}
                    </div>
                </Link>
            )}
            {renderedBy === 'StudioErstellen' ? (
                    <div 
                    className={classes.detailsContainerMobile}
                    style={{...detailsStyle, cursor: isLoading ? 'default' : 'pointer'}}
                >
                    <div 
                        style={titleStyle} 
                        className={`${classes.titleContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}
                    >
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldMiddle">{studio?.StudioName}</span>
                            </>
                        )}
                    </div>
                    <div className={`${classes.locationContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <span className="regularSmall">{studio?.city}</span>
                        )}
                    </div>
                    <div className={`${classes.priceContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldSmall">
                                    {previewDisplayPrice}
                                </span>
                                <span className="regularSmall">pro Stunde</span>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <Link 
                    to={isLoading ? '#' : studioListLinkTo}
                    className={classes.detailsContainerMobile}
                    style={{...detailsStyle, cursor: isLoading ? 'default' : 'pointer'}}
                    onClick={isLoading ? undefined : markOpenedFromSearch}
                >
                    <div 
                        style={titleStyle} 
                        className={`${classes.titleContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}
                    >
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldMiddle">{studio?.StudioName}</span>
                                {renderedBy !== 'StudioErstellen' && studio?.rating ? (
                                    <div className={classes.rattingContainer}>
                                        <span>{studio?.rating}</span>
                                        <img src={starIcon} alt="star"/>
                                    </div>
                                ) : (
                                    <div className={classes.rattingContainer}>
                                        <span>Neu</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${classes.locationContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <span className="regularSmall">{studio?.city}</span>
                        )}
                    </div>
                    <div className={`${classes.priceContainer} ${isLoading || !contentLoaded ? classes.skeleton : ''}`}>
                        {!isLoading && contentLoaded && (
                            <>
                                <span className="semiboldSmall">{backendPriceDisplay.label}</span>
                                <span className="regularSmall">{backendPriceDisplay.unit}</span>
                            </>
                        )}
                    </div>
                </Link>
            )}
        </div>
    );
};

export default StudioProfile;
